/**
 * Main layout component with navigation
 */

import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import styled from 'styled-components';

const LayoutContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  background: var(--color-nav-bg);
  backdrop-filter: var(--nav-backdrop);
  border-bottom: 1px solid var(--color-nav-border);
  box-shadow: var(--shadow-card);
  position: sticky;
  top: 0;
  z-index: 100;
  transition: background 0.2s ease;
`;

const Nav = styled.nav`
  max-width: 1350px;
  margin: 0 auto;
  padding: 0 1.5rem;
  height: 60px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled(Link)`
  font-size: 1.05rem;
  font-weight: 800;
  text-decoration: none;
  color: var(--color-text);
  letter-spacing: -0.02em;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;

  span { color: var(--color-accent); }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 0.15rem;
  align-items: center;
`;

const NavLink = styled(Link)<{ $active?: boolean }>`
  color: ${props => props.$active ? 'var(--color-nav-link-active)' : 'var(--color-nav-link)'};
  background: ${props => props.$active ? 'var(--color-accent-subtle)' : 'transparent'};
  text-decoration: none;
  padding: 0.4rem 0.9rem;
  border-radius: 8px;
  font-size: ${props => props.$active ? '1.05rem' : '0.8rem'};
  font-weight: ${props => props.$active ? '600' : '500'};
  transition: background 0.15s, color 0.15s, font-size 0.2s;

  &:hover {
    background: ${props => props.$active ? 'var(--color-accent-subtle)' : 'var(--color-surface-subtle)'};
    color: ${props => props.$active ? 'var(--color-nav-link-active)' : 'var(--color-text)'};
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
`;

const UserName = styled.span`
  font-size: 0.82rem;
  color: var(--color-text-muted);
  b { color: var(--color-text-secondary); font-weight: 600; }
`;

const LogoutButton = styled.button`
  background: transparent;
  color: var(--color-text-muted);
  border: 1px solid var(--color-border);
  padding: 0.35rem 0.8rem;
  border-radius: 7px;
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 500;
  transition: color 0.15s, border-color 0.15s;

  &:hover {
    color: var(--color-text);
    border-color: var(--color-text-secondary);
  }
`;

const DarkModeToggle = styled.button<{ $isDark: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.8rem;
  border-radius: 20px;
  border: 1px solid var(--color-border);
  background: var(--color-surface-subtle);
  color: var(--color-text-secondary);
  font-size: 0.78rem;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s, background 0.15s;

  &:hover {
    border-color: var(--color-accent);
    color: var(--color-accent);
    background: var(--color-accent-subtle);
  }
`;

const Main = styled.main`
  flex: 1;
  max-width: 1350px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  width: 100%;
`;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <LayoutContainer>
      <Header>
        <Nav>
          <Logo to="/dashboard">
            Movie <span>Recommender</span>
          </Logo>
          <NavLinks>
            <NavLink to="/dashboard"   data-nav-path="/dashboard"   $active={isActive('/dashboard')}>Dashboard</NavLink>
            <NavLink to="/search"      data-nav-path="/search"      $active={isActive('/search')}>Search</NavLink>
            <NavLink to="/ratings"     data-nav-path="/ratings"     $active={isActive('/ratings')}>My Ratings</NavLink>
            <NavLink to="/suggestions" data-nav-path="/suggestions" $active={isActive('/suggestions')}>Recommendations</NavLink>
            <NavLink to="/watchlist"   data-nav-path="/watchlist"   $active={isActive('/watchlist')}>Watchlist</NavLink>
            <NavLink to="/settings"    data-nav-path="/settings"    $active={isActive('/settings')}>Settings</NavLink>
          </NavLinks>
          <RightSection>
            <DarkModeToggle $isDark={isDark} onClick={toggleTheme}>
              {isDark ? '☀️ Light Mode' : '🌙 Dark Mode'}
            </DarkModeToggle>
            <UserName><b>{user?.username}</b></UserName>
            <LogoutButton onClick={handleLogout}>Log out</LogoutButton>
          </RightSection>
        </Nav>
      </Header>
      <Main>{children}</Main>
    </LayoutContainer>
  );
};

export default Layout;
